import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Button,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  GetApp as ExportIcon
} from '@material-ui/icons';
import * as XLSX from 'xlsx';
import { trackPromise } from 'react-promise-tracker';
import Alert from '../../../components/Alert';
import ConsultaPlazasService from '../../../services/ConsultaPlazasService';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  searchField: {
    flex: 1,
  },
  exportButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  headerCell: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    borderBottom: '2px solid #ddd',
  },
}));

const Vista2DetalleSolicitudes = ({ filtrosActivos = {} }) => {
  const classes = useStyles();
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadDataSafe = async () => {
      try {
        setLoading(true);
        const result = await trackPromise(
          ConsultaPlazasService.getPlazasPorCarreraUniversidad()
        );

        if (isMounted) {
          setData(result || []);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error al cargar solicitudes:', error);
          Alert.show({
            message: "Error al cargar el detalle de solicitudes",
            type: "error",
          });
          setData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDataSafe();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    filterData();
  }, [data, searchTerm, filtrosActivos]);



  const filterData = () => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        (item.universidad && item.universidad.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.carrera && item.carrera.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.servicio && item.servicio.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sede && item.sede.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredData(filtered);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      Alert.show({
        message: "No hay datos para exportar",
        type: "warning",
      });
      return;
    }

    const exportData = filteredData.map(item => {
      const estadoSolicitud = item.estado_solicitud || 'Sin Estado';
      
      return {
        "ID Solicitud": item.solicitud_plaza_id || 'N/A',
        "Universidad Código": item.universidad_codigo || 'N/A',
        "Universidad": item.universidad || 'N/A',
        "Sede": item.sede || 'N/A',
        "Servicio Código": item.servicio_codigo || 'N/A',
        "Servicio": item.servicio || 'N/A',
        "Carrera Código": item.carrera_codigo || 'N/A',
        "Carrera": item.carrera || 'N/A',
        "Año": item.anio_carrera || 'N/A',
        "Plazas Solicitadas": item.lugares_solicitados || 0,
        "Estado Solicitud": estadoSolicitud,
        "Número Resolución": item.numero_resolucion || 'N/A',
        "Vigencia": item.vigencia_convenio || 'N/A',
        "Fecha Solicitud": item.fecha_solicitud || 'N/A'
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DetalleSolicitudes");

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `DetalleSolicitudes_${fecha}.xlsx`);

    Alert.show({
      message: "Archivo Excel exportado exitosamente",
      type: "success",
    });
  };

  const getEstadoStyle = (estado) => {
    switch (estado) {
      case 'Aprobado por DNERHS':
        return { backgroundColor: '#e8f5e8', color: '#2e7d32', borderColor: '#2e7d32' };
      case 'Utilizado':
        return { backgroundColor: '#fff3e0', color: '#f57c00', borderColor: '#f57c00' };
      case 'Rechazado por DNERHS':
        return { backgroundColor: '#ffebee', color: '#d32f2f', borderColor: '#d32f2f' };
      case 'Pendiente':
        return { backgroundColor: '#fff8e1', color: '#f9a825', borderColor: '#f9a825' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666', borderColor: '#ddd' };
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Indicador de datos */}
      <Box mb={2} p={2} style={{
        backgroundColor: '#e8f5e8',
        borderRadius: 4,
        border: '2px solid #4caf50',
        textAlign: 'center'
      }}>
        <Typography variant="h6" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
          🏛️ ANÁLISIS POR UNIVERSIDAD: {filteredData.length} solicitudes individuales
        </Typography>
      </Box>

      <Paper>
        {/* Búsqueda y exportación */}
        <Box className={classes.searchBox}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            placeholder="Buscar por universidad, carrera, servicio o sede..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Tooltip title="Exportar a Excel">
            <Button
              variant="contained"
              className={classes.exportButton}
              startIcon={<ExportIcon />}
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
            >
              Exportar
            </Button>
          </Tooltip>
        </Box>
        
        {/* Tabla de solicitudes */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}><strong>ID</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Universidad</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Sede</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Servicio</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Carrera</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Solicitadas</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Convenio / Estado</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => {
                  const estadoSolicitud = item.estado_solicitud || 'Sin Estado';
                  const estadoStyle = getEstadoStyle(estadoSolicitud);
                  
                  return (
                    <TableRow key={index} hover>
                      {/* ID Solicitud */}
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {item.solicitud_plaza_id || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Universidad */}
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {item.universidad || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Código: {item.universidad_codigo || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Sede */}
                      <TableCell>
                        <Typography variant="body2">
                          {item.sede || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Servicio */}
                      <TableCell>
                        <Typography variant="body2">
                          {item.servicio || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Código: {item.servicio_codigo || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Carrera / Año */}
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {item.carrera || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Código: {item.carrera_codigo || 'N/A'}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            Año:
                          </Typography>
                          <Box 
                            component="span" 
                            sx={{
                              backgroundColor: '#e8f5e8',
                              color: '#2e7d32',
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              border: '1px solid #2e7d32'
                            }}
                          >
                            {item.anio_carrera || 'N/A'}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Cantidad Solicitada */}
                      <TableCell align="center">
                        <Chip
                          label={item.lugares_solicitados || 0}
                          style={{
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            minWidth: 50,
                            height: 32,
                            border: '1px solid #2e7d32'
                          }}
                        />
                      </TableCell>

                      {/* Convenio / Estado */}
                      <TableCell>
                        {/* Número de Resolución */}
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="caption" color="textSecondary" style={{ minWidth: '60px' }}>
                            Resolución:
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                            {item.numero_resolucion || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Vigencia */}
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="caption" color="textSecondary" style={{ minWidth: '60px' }}>
                            Vigencia:
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#d84315' }}>
                            {item.vigencia_convenio || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Estado Solicitud */}
                        <Box display="flex" justifyContent="flex-start">
                          <Chip
                            label={estadoSolicitud}
                            size="small"
                            style={{
                              ...estadoStyle,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                              border: `1px solid ${estadoStyle.borderColor}`
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
    </div>
  );
};

export default Vista2DetalleSolicitudes;
